import { Match } from '../types';
import { updateLoadingEffect } from './updateLoadingEffect';

type typographyScore = 'h1' | 'body2';

interface ScoreStyleProps {
  scoreElement: HTMLElement;
  typographyClass?: typographyScore;
}

interface updateMatchScoreProps {
  matchElement: HTMLElement | null;
  matchData: Match;
  matchStatus: string;
  detailMatchPage?: boolean;
  typographyClass?: typographyScore;
}

interface updateScoreProps {
  selector: string;
  newScore: string;
  typographyClass?: typographyScore;
}

/**
 * Aktualizuje třídy CSS elementu ('[data-match-score-1]' | '[data-match-score-2]') pro zobrazení 'live' skóre a upravuje typografii.
 * Upravuje typografii na 'typography--body2-bold'.
 *
 * @param element - HTMLElement, který má být aktualizován.
 * @param typographyClass - Enum označující třídu, která se použije pro typografii skóre. Default je 'body2'.
 */
const updateLiveScoreStyle = ({
  scoreElement,
  typographyClass = 'body2',
}: ScoreStyleProps): void => {
  if (!scoreElement.classList.contains('live-score')) {
    // Přidání třídy 'live-score'
    scoreElement.classList.add('live-score');

    if (typographyClass === 'h1') {
      return;
    } else {
      // Přepnutí typografie na tučnou verzi
      scoreElement.classList.remove(`typography--${typographyClass}`);
      scoreElement.classList.add(`typography--${typographyClass}-bold`);
    }
  }
};

/**
 * Aktualizuje třídy CSS elementu ('[data-match-score-1]' | '[data-match-score-2]') pro zobrazení 'fin' skóre a upravuje typografii.
 *
 * @param element - HTMLElement, který má být aktualizován.
 * @param typographyClass - Enum označující třídu, která se použije pro typografii skóre. Default je 'body2'.
 */
const updateFinScoreStyle = ({
  scoreElement,
  typographyClass = 'body2',
}: ScoreStyleProps): void => {
  // Odebrání třídy 'live-score'
  scoreElement.classList.remove('live-score');

  if (typographyClass === 'h1') {
    return;
  } else {
    scoreElement.classList.remove(`typography--${typographyClass}`);
    scoreElement.classList.add(`typography--${typographyClass}-bold`);
  }
};

/**
 * Funkce pro aktualizaci skóre zápasu.
 * Aktualizuje hodnoty a grafický vzhled skóre v závislosti na stavu zápasu ('live' nebo 'fin').
 *
 * @param matchElement - HTMLElement - zápas, který má být aktualizován.
 * @param matchData - Data zápasu.
 * @param matchStatus - Stav zápasu.
 * @param typographyClass - Enum označující třídu, která se použije pro typografii skóre. Default je 'body2'.
 */
export const updateMatchScore = ({
  matchElement,
  matchData,
  matchStatus,
  typographyClass = 'body2',
}: updateMatchScoreProps) => {
  if (!matchElement) return;

  const matchSport = matchElement.getAttribute('data-match-sport');

  // Aktulazice skóre
  const updateScore = ({ selector, newScore, typographyClass }: updateScoreProps) => {
    const scoreElement = matchElement.querySelector<HTMLElement>(selector);

    if (scoreElement && matchStatus === 'live') {
      updateLiveScoreStyle({ scoreElement, typographyClass });
    } else if (scoreElement && ['fin', 'retired'].includes(matchStatus)) {
      updateFinScoreStyle({ scoreElement, typographyClass });
    }

    if (
      scoreElement &&
      scoreElement.textContent !== newScore &&
      ['live', 'fin', 'retired'].includes(matchStatus)
    ) {
      scoreElement.textContent = newScore;
      updateLoadingEffect(scoreElement);
    }
  };

  // Aktualizace skóre v gemu u tenisu
  const updateGemScore = ({ selector, newScore }: updateScoreProps) => {
    const scoreElement = matchElement.querySelector<HTMLElement>(selector);
    if (scoreElement && matchStatus === 'live') {
      const gameScore = scoreElement.getAttribute('data-match-gem-score');

      if (gameScore !== newScore) {
        scoreElement.setAttribute('data-match-gem-score', newScore);
      }
    }
  };

  // Aktualizace stavu podání u tenisu
  const updateServe = ({ selector, serveId }: { selector: string; serveId?: string }) => {
    const scoreElement = matchElement.querySelector<HTMLElement>(selector);
    if (scoreElement && matchStatus === 'live' && serveId) {
      const playerId = scoreElement.getAttribute('data-player-id');

      if (playerId === serveId) {
        scoreElement.classList.add('serve');
      } else {
        scoreElement.classList.remove('serve');
      }
    } else if (scoreElement && ['fin', 'retired'].includes(matchStatus)) {
      scoreElement.classList.remove('serve');
    }
  };

  if (matchSport === 'ten') {
    // Funkce vrací validní gem-skóre ve tvaru "<část>-<část>", jinak prázdný řetězec.
    // Povolené části: čísla nebo "A" (advantage). Příklady: "A-40", "40-A", "40-30".
    const sanitizeGemScore = (score?: string | null): string => {
      if (!score) return '';
      const s = score.trim();

      // Vrací prázdný řetězec pokud jde o nevalidní formát s dvojtečkou (např. "1:3")
      if (s.includes(':')) return '';

      // Striktní validace formátu "<část>-<část>"
      return /^[Aa0-9]+-[Aa0-9]+$/.test(s) ? s : '';
    };

    // Získání skóre gemu
    const rawGemScore = matchData?.gameScore ?? matchData?.game_score ?? '';
    const gameScoreValue = sanitizeGemScore(rawGemScore);

    // Rozdělení jen tehdy, pokud je hodnota ve tvaru s pomlčkou; jinak vrátí prázdné hodnoty
    const [gameScore1, gameScore2] = gameScoreValue ? gameScoreValue.split('-') : ['', ''];

    // Aktualizace celkového skóre
    updateScore({ selector: '[data-match-score-1]', newScore: matchData.score1, typographyClass });
    updateScore({ selector: '[data-match-score-2]', newScore: matchData.score2, typographyClass });

    // Aktualizace průběžného skóre gemu
    updateGemScore({ selector: '[data-match-score-1]', newScore: gameScore1 });
    updateGemScore({ selector: '[data-match-score-2]', newScore: gameScore2 });

    // Aktualizace podaní
    updateServe({ selector: '[data-match-score-1]', serveId: matchData?.serve });
    updateServe({ selector: '[data-match-score-2]', serveId: matchData?.serve });
  } else if (typographyClass === 'h1') {
    updateScore({
      selector: '[data-match-full-score]',
      newScore: `${matchData.score1} : ${matchData.score2}`,
      typographyClass: 'h1',
    });
  } else {
    updateScore({ selector: '[data-match-score-1]', newScore: matchData.score1, typographyClass });
    updateScore({ selector: '[data-match-score-2]', newScore: matchData.score2, typographyClass });
  }
};
